import React, { useState, useCallback } from 'react';
import { useFlexLayout } from 'react-table';
import PropTypes from 'prop-types';
import ReactTableConstructor from './components/ReactTableConstructor';
import ReactTableCell from './components/ReactTableCell';
import ReactTableCellEditable from './components/ReactTableEditableCell';
import { isEmptyObject, isIterable } from '../../utils/helpers';

const ReactTableBase = ({
  tableConfig,
  columns,
  data,
  updateDraggableData,
  updateEditableData,
}) => {
  const [filterValue, setFilterValue] = useState(null);
  const [customFilterValues, updateCustomFilter] = useState({});

  const globalFilter = useCallback((rows, ids, query) => (
    rows.filter((row) => {
      const { values, original } = row;
      let passesCustomFilterCheck = true;
      if (!isEmptyObject(customFilterValues)) {
        const validKeys = [];
        const customFilterKeys = Object.keys(customFilterValues);
        customFilterKeys.forEach((key) => {
          if (!original[key]) {
            return;
          }
          if (isIterable(original[key])) {
            // Iterate over object and check wether values are equal to one of the custom filters
            const rowValues = original[key];
            const filterValues = customFilterValues[key];
            rowValues.forEach((rowVal) => {
              filterValues.forEach((filterVal) => {
                if (filterVal.id === rowVal || filterVal === rowVal) {
                  validKeys.push(key);
                }
              });
            });
          } else if (customFilterValues[key].some((val) => val === original[key])) {
            validKeys.push(key);
          }
        });
        passesCustomFilterCheck = validKeys.sort().join(',') === customFilterKeys.sort().join(',');
      }
      const disabledFilterColums = columns.filter((c) => c.disableGlobalFilter).map((c) => c.accessor);
      const objectValues = Object.entries(values).filter(([key]) => !disabledFilterColums.includes(key)).map(([, value]) => value);
      return passesCustomFilterCheck && objectValues.some((val) => val.toLowerCase().includes(query.toLowerCase()));
    })
  ), [customFilterValues, columns]);

  const {
    isEditable,
    isResizable,
    isSortable,
    withDragAndDrop,
    withPagination,
    withSearchEngine,
    manualPageSize,
    sortBy,
  } = tableConfig;
  const tableOptions = {
    columns,
    data,
    updateDraggableData,
    updateEditableData,
    updateCustomFilter,
    customFilterValues,
    setFilterValue,
    defaultColumn: {},
    isEditable,
    withDragAndDrop: withDragAndDrop || false,
    dataLength: data.length,
    autoResetPage: true,
    disableSortBy: !isSortable,
    manualSortBy: !isSortable,
    manualGlobalFilter: !withSearchEngine,
    manualPagination: !withPagination,
    globalFilter,
    initialState: {
      pageIndex: 0,
      pageSize: manualPageSize ? manualPageSize[0] : 10,
      globalFilter: withSearchEngine && filterValue ? filterValue : undefined,
      hiddenColumns: columns
        .filter((col) => col.hidden === true)
        .map((col) => col.accessor),
      sortBy,
    },
  };

  let tableOptionalHook = [];
  if (isResizable) tableOptionalHook = [useFlexLayout];
  if (withSearchEngine) {
    tableOptions.defaultColumn = {
      Cell: ReactTableCell,
    };
  }
  if (isEditable) {
    tableOptions.defaultColumn = {
      Cell: ReactTableCellEditable,
    };
  }

  return (
    <ReactTableConstructor
      key={isResizable || isEditable ? 'modified' : 'common'}
      tableConfig={tableConfig}
      tableOptions={tableOptions}
      tableOptionalHook={tableOptionalHook}
    />
  );
};

ReactTableBase.propTypes = {
  tableConfig: PropTypes.shape({
    isEditable: PropTypes.bool,
    isResizable: PropTypes.bool,
    isSortable: PropTypes.bool,
    withDragAndDrop: PropTypes.bool,
    withPagination: PropTypes.bool,
    withSearchEngine: PropTypes.bool,
    manualPageSize: PropTypes.arrayOf(PropTypes.number),
    sortBy: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool,
    })),
    customHeaderButton: PropTypes.element,
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
  })),
  data: PropTypes.arrayOf(PropTypes.shape()),
  updateDraggableData: PropTypes.func,
  updateEditableData: PropTypes.func,
};

ReactTableBase.defaultProps = {
  tableConfig: {
    isEditable: false,
    isResizable: false,
    isSortable: false,
    withDragAndDrop: false,
    withPagination: false,
    withSearchEngine: false,
    manualPageSize: [],
    sortBy: [],
    customHeaderButton: null,
  },
  columns: [],
  data: [],
  updateDraggableData: () => {},
  updateEditableData: () => {},
};

export default ReactTableBase;
